<template>
    <div class="tabs-container">
        <div class="tabs-row">
            <div class="tab-fixed" :class="{ 'tab-fixed-active': selectedIndex === 0 }" @click="goToListTab">
                <span>Contacte</span>
            </div>
            <DxTabs
                v-if="contactTabs.length > 0"
                class="tabs-scroll"
                :data-source="contactTabs"
                :selected-index="selectedIndex - 1"
                item-template="tabItem"
                :scrolling-enabled="true"
                :show-nav-buttons="true"
                @option-changed="onTabChanged"
            >
                <template #tabItem="{ data, index }">
                    <div class="tab-label">
                        <span>{{ data.text }}</span>
                        <span class="tab-close" @click.stop="closeTab(index)">×</span>
                    </div>
                </template>
            </DxTabs>
        </div>

        <div v-show="selectedIndex === 0" class="tab-content">
            <GridTabContacte @select-contact="onContactSelected" class="grid-fill" />
        </div>

        <template v-for="(c, i) in openContacts" :key="c.contactId">
            <div v-if="selectedIndex === i + 1" class="tab-content">
                <GridContactDetalii :contact="c.contactData" class="fill" @back="goToListTab" />
            </div>
        </template>
    </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { DxTabs } from 'devextreme-vue/tabs';
import GridTabContacte from './GridTabContacte.vue';
import GridContactDetalii from './GridContactDetalii.vue';
import CrmContacts from '@/services/Comercial/CrmContacts';
import DataSource from 'devextreme/data/data_source';
import AppSetting from '@/settings/appSettings';

interface OpenContactTab {
    contactId: string;
    contactData: Record<string, any>;
}

export default defineComponent({
    components: {
        DxTabs,
        GridTabContacte,
        GridContactDetalii
    },
    data() {
        return {
            selectedIndex: 0,
            openContacts: [] as OpenContactTab[],
            contactTabs: [] as { text: string, contactId: string }[],
        };
    },
    mounted() {
        if (this.$route.query.contactId) {
            const id = this.$route.query.contactId as string;
            this.fetchContact(id);
        }
    },
    watch: {
        '$route.query.contactId': {
            handler(newId) {
                if (newId) {
                    this.fetchContact(newId as string);
                }
            },
        }
    },
    methods: {
        onTabChanged(e: any) {
            if (e.name !== 'selectedIndex') return;
            const internalIndex = e.component.option('selectedIndex');
            if (internalIndex == null || internalIndex < 0) return;
            this.selectedIndex = internalIndex + 1;
        },
        goToListTab() {
            this.selectedIndex = 0;
        },
        onContactSelected(contact: any) {
            this.openContact(contact);
        },
        openContact(contact: any) {
            const id = String(contact.ID);
            const existingIndex = this.openContacts.findIndex(c => c.contactId === id);
            if (existingIndex !== -1) {
                this.selectedIndex = existingIndex + 1;
                return;
            }
            const label = contact.Nume ? `${contact.Nume}${contact.Prenume ? ' ' + contact.Prenume : ''}` : 'Contact';
            this.openContacts.push({ contactId: id, contactData: contact });
            this.contactTabs = [...this.contactTabs, { text: label, contactId: id }];
            this.selectedIndex = this.contactTabs.length;
        },
        closeTab(index: number) {
            const wasActiveTab = this.selectedIndex === index + 1;
            const activeContactId = wasActiveTab ? null : this.openContacts[this.selectedIndex - 1]?.contactId;

            this.openContacts.splice(index, 1);
            this.contactTabs = this.contactTabs.filter((_, i) => i !== index);

            if (wasActiveTab) {
                // Închizi contactul pe care îl vizualizai — te întorci mereu pe gridul de contacte
                this.selectedIndex = 0;
            } else {
                // Închizi un tab din fundal — rămâi pe contactul curent, doar recalculezi indexul
                const newIndex = activeContactId
                    ? this.openContacts.findIndex(c => c.contactId === activeContactId) + 1
                    : 0;
                this.selectedIndex = newIndex;
            }
        },
        fetchContact(id: string) {
            if (!id || id === '') return;
            const ds = new DataSource({
                store: {
                    type: 'odata',
                    url: AppSetting.API_Url_ComercialOdata + 'CRM_Contacts',
                    key: 'ID',
                    keyType: 'Guid',
                    version: 4,
                    beforeSend: (e: any) => {
                        e.headers = {
                            'Content-Type': 'application/json',
                            Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt'),
                        };
                        e.params.$expand = 'Account($select=Nume),Tara($select=Nume),Localitate($select=Localitate),Judet($select=Nume)';
                    },
                },
                filter: ['ID', '=', id],
            });

            ds.load().then((response) => {
                if (response && response.length > 0) {
                    this.openContact(response[0]);
                }
            }).catch(error => {
                console.error("Error fetching contact:", error);
            });
        }
    }

});
</script>
<style scoped>
.tabs-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.tabs-row {
    display: flex;
    align-items: stretch;
    flex-shrink: 0;
}

.tab-fixed {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 16px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
}

.tab-fixed:hover {
    color: rgba(0, 0, 0, 0.87);
}

.tab-fixed-active {
    color: #ff2c21;
    border-bottom-color: #ff2c21;
    font-weight: 600;
}

.tabs-scroll {
    flex: 1;
    min-width: 0;
}

.tab-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.grid-fill {
    flex: 1;
    min-height: 0;
}

.fill {
    flex: 1;
    min-height: 0;
    width: 100%;
}

.tab-label {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tab-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 14px;
    line-height: 1;
    color: #94a3b8;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.tab-close:hover {
    background: #fee2e2;
    color: #ef4444;
}
</style>
